import { useEffect, useState } from "react";
import { Timer, Megaphone, Check, X, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage, formatTanggalID } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalChain } from "@/components/ApprovalChain";



export default function Lembur() {
  const [rows, setRows] = useState([]);
  const [quota, setQuota] = useState(null);
  const [perintahPending, setPerintahPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [actingId, setActingId] = useState(null);

  async function load() {
    try {
      const [mine, q, pp] = await Promise.all([
        api.get("/overtime/mine"),
        api.get("/overtime/quota"),
        api.get("/overtime/perintah/pending"),
      ]);
      setRows(mine.data);
      setQuota(q.data);
      setPerintahPending(pp.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function acceptPerintah(row) {
    if (!window.confirm(`Terima perintah lembur ${formatTanggalID(row.tanggal)} (${row.rencana_mulai}–${row.rencana_selesai}) dari ${row.perintah_dari_nama || "atasan"}?`)) return;
    setActingId(row.id);
    try {
      await api.post(`/overtime/${row.id}/accept-perintah`);
      toast.success("Perintah lembur diterima — langsung DISETUJUI & aktif (tanpa persetujuan ulang atasan)");
      await load();
      window.dispatchEvent(new Event("atlas:refresh-approvals"));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setActingId(null);
    }
  }

  async function submitDecline() {
    if (declineReason.trim().length < 3) {
      toast.error("Alasan penolakan minimal 3 karakter");
      return;
    }
    setActingId(declineTarget.id);
    try {
      await api.post(`/overtime/${declineTarget.id}/decline-perintah`, {
        alasan: declineReason.trim(),
      });
      toast.success("Perintah lembur ditolak");
      setDeclineTarget(null);
      setDeclineReason("");
      await load();
      window.dispatchEvent(new Event("atlas:refresh-approvals"));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-lembur">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Lembur
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Riwayat &amp; Perintah Lembur
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Lembur hanya dapat terjadi melalui <b>Perintah Lembur</b> dari Atasan/Admin. Terima perintah yang masuk agar lembur langsung <b>Disetujui &amp; aktif</b>, atau tolak dengan alasan.
          </p>
        </div>
      </div>

      {/* Inbox: Perintah Lembur dari Atasan */}
      {perintahPending.length > 0 ? (
        <div
          className="mt-6 rounded-xl border border-amber-300 bg-amber-50/60 p-5"
          data-testid="perintah-inbox"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg bg-amber-100 border border-amber-300 flex items-center justify-center text-amber-800">
              <Megaphone size={17} />
            </div>
            <div>
              <div className="font-display text-base font-extrabold text-slate-900 leading-tight">
                Perintah Lembur untuk Anda
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Terima agar lembur langsung <b>Disetujui &amp; aktif</b> (tanpa persetujuan ulang atasan), atau Tolak dengan alasan.
              </p>
            </div>
            <span className="ml-auto text-[10px] font-bold text-amber-900 bg-amber-200/70 rounded-full px-2 py-0.5">
              {perintahPending.length} MENUNGGU KONFIRMASI
            </span>
          </div>
          <div className="space-y-3">
            {perintahPending.map((r) => (
              <div
                key={r.id}
                className="rounded-lg border border-amber-200 bg-white p-4"
                data-testid={`perintah-item-${r.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">
                      {formatTanggalID(r.tanggal)}{" "}
                      <span className="ml-1 text-sm font-normal font-mono-clock text-slate-700">
                        · {r.rencana_mulai}–{r.rencana_selesai}
                      </span>
                      <span className="ml-1 text-xs text-slate-400">({r.rencana_jam} jam)</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Diperintah oleh <b className="text-slate-800">{r.perintah_dari_nama || "atasan"}</b>
                    </div>
                    <div className="text-sm text-slate-700 mt-2 whitespace-pre-line">
                      {r.perintah_uraian || r.alasan}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => acceptPerintah(r)}
                      disabled={actingId === r.id}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                      data-testid={`perintah-accept-${r.id}`}
                    >
                      <Check size={13} /> Terima
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setDeclineTarget(r); setDeclineReason(""); }}
                      disabled={actingId === r.id}
                      className="border-rose-200 text-rose-700 hover:bg-rose-50 gap-1"
                      data-testid={`perintah-decline-${r.id}`}
                    >
                      <X size={13} /> Tolak
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Info auto-record */}
      <div className="mt-6 rounded-md border border-teal-200 bg-teal-50/50 p-3.5 flex items-start gap-2 text-xs text-teal-900" data-testid="lembur-auto-info">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          Setelah pengajuan disetujui, waktu <b>Mulai Lembur</b> otomatis mengikuti jam Rencana Mulai, dan <b>Selesai Lembur</b> otomatis tercatat saat Anda melakukan Check-Out absensi harian.
        </span>
      </div>

      {/* Quota card */}
      {quota ? (
        <div
          className="mt-6 rounded-xl border border-teal-200 bg-teal-50/40 p-5 flex flex-wrap items-center justify-between gap-4"
          data-testid="lembur-quota-card"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-teal-700 mb-1">
              Kuota Bulan {String(quota.month).padStart(2, "0")}/{quota.year}
            </div>
            <div className="font-mono-clock text-3xl font-bold text-slate-900">
              {quota.used_actual_hours} <span className="text-slate-400 text-lg">/ {quota.kuota_bulanan_jam} jam</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Sisa: <b className="text-teal-800">{quota.remaining_hours} jam</b> (dihitung dari jam aktual)
            </div>
          </div>
          <div className="w-full sm:w-64">
            <div className="h-2.5 rounded-full bg-teal-100 overflow-hidden">
              <div
                className="h-full bg-teal-600 transition-all"
                style={{
                  width: `${Math.min(100, (quota.used_actual_hours / (quota.kuota_bulanan_jam || 1)) * 100)}%`,
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="text-center text-slate-500 py-14">Memuat…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white text-center py-14">
            <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <Timer size={22} />
            </div>
            <div className="mt-3 font-semibold text-slate-700">Belum ada catatan lembur</div>
            <div className="text-xs text-slate-500 mt-1">
              Riwayat lembur akan tampil di sini setelah Anda menerima <b>Perintah Lembur</b> dari Atasan/Admin.
            </div>
          </div>
        ) : (
          rows.map((r) => {
            return (
              <div
                key={r.id}
                className="rounded-xl border border-slate-200 bg-white p-5"
                data-testid={`lembur-card-${r.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={r.status} testId={`lembur-status-${r.id}`} />
                      <span className="text-[10px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                        {r.timezone}
                      </span>
                      {r.auto_mulai || r.auto_selesai ? (
                        <span className="text-[10px] font-semibold text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                          Auto-Record
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 font-semibold text-slate-900">
                      {formatTanggalID(r.tanggal)}
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">
                      Rencana: <span className="font-mono-clock">{r.rencana_mulai} → {r.rencana_selesai}</span>{" "}
                      <span className="text-xs text-slate-400">({r.rencana_jam} jam)</span>
                    </div>
                    {r.actual_mulai_lokal || r.actual_selesai_lokal ? (
                      <div className="text-sm text-slate-700 mt-0.5">
                        Aktual:{" "}
                        <span className="font-mono-clock">
                          {r.actual_mulai_lokal || "—"} → {r.actual_selesai_lokal || "menunggu Check-Out…"}
                        </span>{" "}
                        {r.actual_jam != null ? (
                          <span className="text-xs text-teal-700 font-semibold">({r.actual_jam} jam)</span>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="text-sm text-slate-600 mt-1">{r.alasan}</div>
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Diajukan {formatTanggalID((r.created_at || "").slice(0, 10))}
                  </div>
                </div>
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
                    Alur Persetujuan
                  </div>
                  <ApprovalChain chain={r.approval_chain} currentLevel={r.current_level} status={r.status} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Dialog: tolak perintah lembur */}
      <Dialog open={!!declineTarget} onOpenChange={(v) => !v && setDeclineTarget(null)}>
        <DialogContent className="max-w-md" data-testid="perintah-decline-dialog">
          <DialogHeader>
            <DialogTitle>Tolak Perintah Lembur</DialogTitle>
            <DialogDescription>
              {declineTarget ? `${formatTanggalID(declineTarget.tanggal)} · ${declineTarget.rencana_mulai}–${declineTarget.rencana_selesai}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Penolakan (wajib)
            </Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              placeholder="Contoh: Ada acara keluarga tidak dapat diundur…"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              data-testid="perintah-decline-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineTarget(null)}>Batal</Button>
            <Button
              onClick={submitDecline}
              disabled={actingId === declineTarget?.id}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="perintah-decline-confirm-btn"
            >
              Tolak Perintah
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
